package kabinet.utils

import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.LocalTime
import kotlinx.datetime.Month
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.Duration
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Duration.Companion.seconds
import kotlin.time.Instant

fun Instant.toLocalDateTimeUtc() = toLocalDateTime(TimeZone.UTC).let { time ->
    LocalDateTime(
        year = time.year,
        month = time.month,
        day = time.day,
        hour = time.hour,
        minute = time.minute,
        second = time.second,
        nanosecond = 0
    )
}

fun LocalDateTime.toInstantFromUtc() = this.toInstant(TimeZone.UTC)

fun LocalDateTime.toInstantFromLocal() = this.toInstant(TimeZone.currentSystemDefault())

fun Instant.toLocalDateTime(isLocalTime: Boolean = false) = this.toLocalDateTime(if (isLocalTime) TimeZone.UTC else TimeZone.currentSystemDefault())

fun Instant.toLocalDate() = this.toLocalDateTime().date

fun Instant.toAgoFormat() = (Clock.System.now() - this).toAgoFormat()

fun Instant.toFutureFormat(): String {
    val time = this.toLocalDateTime()
    val now = Clock.System.now()
    val today = now.toLocalDateTime()
    if (time.date == today.date) return "Today"
    val tomorrow = (now + 1.days).toLocalDateTime()
    if (time.date == tomorrow.date) return "Tomorrow"
    val nextWeek = (now + 7.days).toLocalDateTime()
    if (time < nextWeek) return time.dayOfWeek.toLongFormat()
    val nextYear = (now + 365.days).toLocalDateTime()
    if (time < nextYear) return "${time.dayOfWeek.toShortFormat()}, ${time.month.toShortFormat()}. ${time.day}"
    else return "${time.month}/${time.day}/${time.year}"
}

fun Instant.toPastFormat(): String {
    val time = this.toLocalDateTime()
    val now = Clock.System.now()
    val today = now.toLocalDateTime()
    if (time.date == today.date) return time.toHourAndMinutesFormat()
    val yesterday = (now - 1.days).toLocalDateTime()
    if (time.date == yesterday.date) return "Yesterday"
    val lastWeek = (now - 7.days).toLocalDateTime()
    if (time > lastWeek) return time.dayOfWeek.toLongFormat()
    val lastYear = (now - 365.days).toLocalDateTime()
    if (time > lastYear) return "${time.dayOfWeek.toShortFormat()}, ${time.month.toShortFormat()}. ${time.day}"
    else return "${time.month}/${time.day}/${time.year}"
}

fun LocalDate.toFutureFormat(useDayOfWeek: Boolean = true): String {
    val now = Clock.System.now()
    val today = now.toLocalDate()
    if (this == today) return "Today"
    val tomorrow = (now + 1.days).toLocalDate()
    if (this == tomorrow) return "Tomorrow"
    if (useDayOfWeek) {
        val nextWeek = (now + 7.days).toLocalDate()
        if (this < nextWeek) return dayOfWeek.toLongFormat()
    }
    val nextYear = (now + 365.days).toLocalDate()
    if (this < nextYear) return "${dayOfWeek.toShortFormat()}, ${month.toShortFormat()}. $day"
    else return "${month}/${day}/${year}"
}

fun Instant.toTimeFormat(isLocalTime: Boolean = false) = this.toLocalDateTime(isLocalTime)
    .let { "${it.hour12}:${it.minute.toString().padStart(2, '0')} ${it.amPmLabel}" }

fun LocalTime.toTimeFormat(): String = when {
    hour == 12 && minute == 0 -> "Noon"
    hour == 0 && minute == 0 -> "Midnight"
    else -> {
        val displayHour = when (hour % 12) {
            0 -> 12
            else -> hour % 12
        }
        val suffix = if (hour < 12) "AM" else "PM"
        "$displayHour:${minute.toString().padStart(2, '0')} $suffix"
    }
}

fun Instant.toRelativeTimeFormat(isLocalTime: Boolean = false) = "${this.toFutureFormat()} at ${this.toTimeFormat(isLocalTime)}"

val LocalDateTime.hour12 get() = if (hour == 0) 12 else if (hour > 12) hour - 12 else hour

val LocalDateTime.isPm get() = hour >= 12

val LocalDateTime.amPmLabel get() = if (isPm) "PM" else "AM"

fun DayOfWeek.toShortFormat() = when (this) {
    DayOfWeek.MONDAY -> "Mon"
    DayOfWeek.TUESDAY -> "Tue"
    DayOfWeek.WEDNESDAY -> "Wed"
    DayOfWeek.THURSDAY -> "Thu"
    DayOfWeek.FRIDAY -> "Fri"
    DayOfWeek.SATURDAY -> "Sat"
    DayOfWeek.SUNDAY -> "Sun"
    else -> error("no short format: $this")
}

fun DayOfWeek.toLongFormat() = when (this) {
    DayOfWeek.MONDAY -> "Monday"
    DayOfWeek.TUESDAY -> "Tuesday"
    DayOfWeek.WEDNESDAY -> "Wednesday"
    DayOfWeek.THURSDAY -> "Thursday"
    DayOfWeek.FRIDAY -> "Friday"
    DayOfWeek.SATURDAY -> "Saturday"
    DayOfWeek.SUNDAY -> "Sunday"
    else -> error("no long format: $this")
}

fun Month.toShortFormat() = when (this) {
    Month.JANUARY -> "Jan"
    Month.FEBRUARY -> "Feb"
    Month.MARCH -> "Mar"
    Month.APRIL -> "Apr"
    Month.MAY -> "May"
    Month.JUNE -> "Jun"
    Month.JULY -> "Jul"
    Month.AUGUST -> "Aug"
    Month.SEPTEMBER -> "Sep"
    Month.OCTOBER -> "Oct"
    Month.NOVEMBER -> "Nov"
    Month.DECEMBER -> "Dec"
    else -> error("no short format: $this")
}

fun Month.toLongFormat() = when (this) {
    Month.JANUARY -> "January"
    Month.FEBRUARY -> "February"
    Month.MARCH -> "March"
    Month.APRIL -> "April"
    Month.MAY -> "May"
    Month.JUNE -> "June"
    Month.JULY -> "July"
    Month.AUGUST -> "August"
    Month.SEPTEMBER -> "September"
    Month.OCTOBER -> "October"
    Month.NOVEMBER -> "November"
    Month.DECEMBER -> "December"
    else -> error("no long format: $this")
}

fun Instant.toDoubleMillis(): Double = toEpochMilliseconds().toDouble()
fun Instant.Companion.fromDoubleMillis(d: Double): Instant =
    Instant.fromEpochMilliseconds(d.toLong())

fun Instant.toHourAndMinutesFormat() = toLocalDateTime().toHourAndMinutesFormat()
fun LocalDateTime.toHourAndMinutesFormat() = "$hour12:${minute.toString().padStart(2, '0')} $amPmLabel"

fun Instant.toDayDescription() = toLocalDateTime().toDayDescription()
fun LocalDateTime.toDayDescription() = "${dayOfWeek.toLongFormat()} the ${day.toOrdinalSuffix()} of ${month.toLongFormat()}"

fun Instant.Companion.fromEpochSecondsDouble(value: Double) = Instant.fromEpochSeconds(value.toLong())

private val REL_RE = Regex("""^\s*(\d+)\s+(second|minute|hour|day|month|year)s?\s+ago\s*$""", RegexOption.IGNORE_CASE)

fun Instant.Companion.fromAgoFormat(input: String, zone: TimeZone = TimeZone.currentSystemDefault()): Instant? {
    val match = REL_RE.matchEntire(input) ?: return null
    val amount = match.groupValues[1].toLong()
    val unit = match.groupValues[2].lowercase()
    val now = Clock.System.now()

    return when (unit) {
        "second" -> now - amount.seconds
        "minute" -> now - amount.minutes
        "hour"   -> now - amount.hours
        "day"    -> now - amount.days
        "month"  -> now.minus(amount, DateTimeUnit.MONTH, zone)
        "year"   -> now.minus(amount, DateTimeUnit.YEAR, zone)
        else     -> null
    }
}

fun Instant.olderThan(duration: Duration) = Clock.System.now() - this > duration